/**
 * อ่าน Tiled .tmj — collision เป็น "data" ไม่ใช่โค้ด (CLAUDE.md กฎเหล็กข้อ 13)
 * แก้ผังต้องแก้ที่ tools/gen/*.py แล้ว run gen_tiled.py ใหม่ ห้าม hardcode ที่นี่
 */
import { SCENE_H, SCENE_W, type SceneId, type SceneMode } from "@py/protocol";

export type TmjProperty = { name: string; type: string; value: unknown };

export type TmjObject = {
  id: number;
  name: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  point?: boolean;
  properties?: TmjProperty[];
};

export type TmjLayer =
  | { type: "tilelayer"; name: string; width: number; height: number; data: number[] }
  | { type: "objectgroup"; name: string; objects: TmjObject[] };

export type Tmj = {
  width: number;
  height: number;
  tilewidth: number;
  tileheight: number;
  layers: TmjLayer[];
  properties?: TmjProperty[];
};

export type OneWayRect = {
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type SceneMap = {
  scene: SceneId;
  name: string;
  mode: SceneMode;
  groundY: number;
  cols: number;
  rows: number;
  tileW: number;
  tileH: number;
  /** null เมื่อฉากนั้นไม่มี tile layer (ฉาก 0, 4, 5 ใช้ object แทน) */
  solid: Uint8Array | null;
  oneWay: OneWayRect[];
  spawn: { x: number; y: number };
};

function prop<T>(props: TmjProperty[] | undefined, name: string, fallback: T): T {
  const p = props?.find((q) => q.name === name);
  return p === undefined ? fallback : (p.value as T);
}

export function parseTmj(raw: Tmj): SceneMap {
  const scene = prop(raw.properties, "scene", 0) as SceneId;
  const tileLayer = raw.layers.find(
    (l): l is Extract<TmjLayer, { type: "tilelayer" }> =>
      l.type === "tilelayer" && l.name === "collision",
  );
  const objLayer = raw.layers.find(
    (l): l is Extract<TmjLayer, { type: "objectgroup" }> => l.type === "objectgroup",
  );

  let solid: Uint8Array | null = null;
  if (tileLayer) {
    solid = new Uint8Array(tileLayer.width * tileLayer.height);
    for (let i = 0; i < solid.length; i++) solid[i] = tileLayer.data[i] === 1 ? 1 : 0;
  }

  const objects = objLayer?.objects ?? [];
  const oneWay: OneWayRect[] = objects
    .filter((o) => o.type === "oneway")
    .map((o) => ({ label: o.name, x: o.x, y: o.y, width: o.width, height: o.height }));

  const spawnObj = objects.find((o) => o.type === "spawn");

  return {
    scene,
    name: prop(raw.properties, "sceneName", ""),
    mode: prop(raw.properties, "mode", "platformer") as SceneMode,
    groundY: prop(raw.properties, "groundY", 1560),
    cols: raw.width,
    rows: raw.height,
    tileW: raw.tilewidth,
    tileH: raw.tileheight,
    solid,
    oneWay,
    spawn: { x: spawnObj?.x ?? SCENE_W / 2, y: spawnObj?.y ?? 1560 },
  };
}

/** แปลงพิกัด native → ช่องกริด (ASSETS.md §3) */
export function tileAt(map: SceneMap, x: number, y: number): { col: number; row: number } {
  return {
    col: Math.floor(x / (SCENE_W / map.cols)),
    row: Math.floor(y / (SCENE_H / map.rows)),
  };
}

export function isSolidAt(map: SceneMap, x: number, y: number): boolean {
  if (!map.solid) return false;
  const { col, row } = tileAt(map, x, y);
  if (col < 0 || col >= map.cols || row < 0 || row >= map.rows) return false;
  return map.solid[row * map.cols + col] === 1;
}

/** รูปแบบดิบที่ tools/gen ปล่อยออกมา — ใช้เทียบกับ .tmj ใน test P1 */
export type CollisionJson = {
  scene: number;
  name: string;
  mode: string;
  grid?: [number, number];
  solid?: string[];
  platforms?: { x0: number; x1: number; y: number; label?: string }[];
  spawn?: [number, number];
  ground?: number;
};

export function solidFromCollisionJson(src: CollisionJson): Uint8Array | null {
  if (!src.solid || !src.grid) return null;
  const [cols, rows] = src.grid;
  const out = new Uint8Array(cols * rows);
  for (let r = 0; r < rows; r++) {
    const row = src.solid[r] ?? "";
    for (let c = 0; c < cols; c++) out[r * cols + c] = row[c] === "1" ? 1 : 0;
  }
  return out;
}
